import type { Vector2 } from '@sorrem/shared';
import { useEffect, useId, useRef, type PointerEvent as ReactPointerEvent } from 'react';

/**
 * Drag and drop built on pointer events, in place of the browser's native
 * drag and drop: during a native drag Chrome shows its own system cursor,
 * which CSS cannot replace. Here the drag's cursor (see `cursors.scss`)
 * shows everywhere while it lasts, and a faded copy of the source follows
 * the pointer.
 *
 * Pressing a source (`useDragSource`) selects no text. Its cursor shows once
 * the press is held DRAG_HOLD_MS, or the drag starts. The drag itself (the
 * ghost, onStart) starts once the pointer has moved DRAG_SLOP_PX from where
 * it was pressed, so a press let go in place is still a click; the click the browser fires
 * at the end of a drag is swallowed. Targets (`useDropTarget`) are found
 * under the pointer by a marker attribute.
 */

/** How far (CSS px) the pointer travels before a press becomes a drag. */
const DRAG_SLOP_PX = 4;
/** How long (ms) a press is held before the drag's cursor shows, so a quick click never flashes it. */
const DRAG_HOLD_MS = 250;
const DROP_TARGET_ATTRIBUTE = 'data-drop-target';
/** Set on `<html>` from a held press or drag until it ends; `cursors.scss` shows its cursor everywhere. */
const DRAG_CURSOR_ATTRIBUTE = 'data-drag-cursor';

/** Picking something up to carry it (`grabbing`), or repositioning it (`move`). */
export type DragCursor = 'grabbing' | 'move';

export interface DragSourceOptions {
    readonly cursor: DragCursor;
    /** The element whose copy follows the pointer (e.g. just the item in a slot). The source when unset or null. */
    readonly ghostOf?: () => HTMLElement | null;
    /** The press became a drag. */
    readonly onStart: () => void;
    /** The drag ended, after any drop. */
    readonly onEnd?: () => void;
}

export interface DropTargetOptions {
    /**
     * The pointer is over this target during a drag, at `point` within
     * `rect` (the target's bounds). Return false to refuse a drop here.
     */
    readonly onOver?: (point: Vector2, rect: DOMRect) => boolean | void;
    /** Something was dropped on this target. */
    readonly onDrop: () => void;
}

const dropTargets = new Map<string, { current: DropTargetOptions }>();

/**
 * The drop target under `point`, if it takes a drop there. Its ref, not its
 * options: a drop reads the options as they are by then, after any render
 * the drag's start caused.
 */
function resolveDropTarget(point: Vector2): { current: DropTargetOptions } | null {
    const element = document.elementFromPoint(point.x, point.y)?.closest(`[${DROP_TARGET_ATTRIBUTE}]`);
    const id = element?.getAttribute(DROP_TARGET_ATTRIBUTE);
    const target = id ? dropTargets.get(id) : undefined;
    if (!element || !target) {
        return null;
    }
    return target.current.onOver?.(point, element.getBoundingClientRect()) === false ? null : target;
}

/**
 * A faded copy of the source that follows the pointer, zoomed as the source
 * is (a HUD panel zooms by --hud-scale); its lengths are in its own zoomed
 * pixels, so screen distances are divided by the zoom.
 */
function createGhost(source: HTMLElement): HTMLElement {
    const rect = source.getBoundingClientRect();
    const zoom = source.currentCSSZoom || 1;
    const ghost = source.cloneNode(true) as HTMLElement;
    ghost.setAttribute('aria-hidden', 'true');
    Object.assign(ghost.style, {
        position: 'fixed',
        zoom: String(zoom),
        left: `${rect.left / zoom}px`,
        top: `${rect.top / zoom}px`,
        width: `${rect.width / zoom}px`,
        height: `${rect.height / zoom}px`,
        margin: '0',
        opacity: '0.75',
        pointerEvents: 'none',
        zIndex: 'var(--layer-drag)',
    });
    document.body.append(ghost);
    return ghost;
}

/** Keeps a press from starting a text selection. */
function preventSelection(event: Event): void {
    event.preventDefault();
}

/** Swallows the click the browser fires when the drag's press is let go. */
function suppressNextClick(): void {
    const swallow = (event: MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
    };
    window.addEventListener('click', swallow, { capture: true, once: true });
    // A release with no click (it landed on another element) leaves nothing to swallow.
    setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

/**
 * Makes an element a drag source: spread the result onto it. Undefined
 * options make it an ordinary element.
 */
export function useDragSource(options: DragSourceOptions | undefined): { onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void } {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const cleanupRef = useRef<(() => void) | null>(null);

    // An unmount mid-drag (e.g. the panel closed) ends the drag without a drop.
    useEffect(() => () => cleanupRef.current?.(), []);

    if (options === undefined) {
        return {};
    }

    return {
        onPointerDown: (event: ReactPointerEvent<HTMLElement>): void => {
            if (event.button !== 0 || cleanupRef.current) {
                return;
            }
            const source = event.currentTarget;
            const start: Vector2 = { x: event.clientX, y: event.clientY };
            let ghost: HTMLElement | null = null;
            let target: { current: DropTargetOptions } | null = null;
            const showCursor = (): void => {
                const cursor = optionsRef.current?.cursor;
                if (cursor) {
                    document.documentElement.setAttribute(DRAG_CURSOR_ATTRIBUTE, cursor);
                }
            };

            const move = (moveEvent: PointerEvent): void => {
                const point: Vector2 = { x: moveEvent.clientX, y: moveEvent.clientY };
                if (!ghost) {
                    if (Math.hypot(point.x - start.x, point.y - start.y) < DRAG_SLOP_PX) {
                        return;
                    }
                    const current = optionsRef.current;
                    if (!current) {
                        finish(false);
                        return;
                    }
                    clearTimeout(holdTimer);
                    showCursor();
                    ghost = createGhost(current.ghostOf?.() ?? source);
                    current.onStart();
                }
                const zoom = Number(ghost.style.zoom) || 1;
                ghost.style.transform = `translate(${(point.x - start.x) / zoom}px, ${(point.y - start.y) / zoom}px)`;
                target = resolveDropTarget(point);
            };

            const release = (): void => finish(true);
            const cancel = (): void => finish(false);

            const finish = (dropped: boolean): void => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', release);
                window.removeEventListener('pointercancel', cancel);
                document.removeEventListener('selectstart', preventSelection);
                clearTimeout(holdTimer);
                document.documentElement.removeAttribute(DRAG_CURSOR_ATTRIBUTE);
                cleanupRef.current = null;
                if (!ghost) {
                    return;
                }
                ghost.remove();
                suppressNextClick();
                if (dropped) {
                    target?.current.onDrop();
                }
                optionsRef.current?.onEnd?.();
            };

            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', release);
            window.addEventListener('pointercancel', cancel);
            // From the press, not the drag: a selection begun inside the slop would stay highlighted.
            document.addEventListener('selectstart', preventSelection);
            const holdTimer = setTimeout(showCursor, DRAG_HOLD_MS);
            cleanupRef.current = cancel;
        },
    };
}

/**
 * Makes an element a drop target: spread the result onto it. Undefined
 * options make it an ordinary element.
 */
export function useDropTarget(options: DropTargetOptions | undefined): { [DROP_TARGET_ATTRIBUTE]?: string } {
    const id = useId();
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const isTarget = options !== undefined;

    useEffect(() => {
        if (!isTarget) {
            return undefined;
        }
        // Registered only while there are options, so `current` is set whenever it is read.
        dropTargets.set(id, optionsRef as { current: DropTargetOptions });
        return () => {
            dropTargets.delete(id);
        };
    }, [id, isTarget]);

    return isTarget ? { [DROP_TARGET_ATTRIBUTE]: id } : {};
}
